import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { equipmentService, warehouseService, workstationService } from 'services/api';
import { IdentityField } from 'components/form/IdentityField';
import { IAccessor, IEquipment, IOption, IStatusOption } from 'interfaces';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { useEffect, useState } from 'react';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { useTranslation } from 'react-i18next';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import SectionTitle from 'components/common/SectionTitle';
import { CurrencyTextFieldAdapter } from 'components/form/CurrencyTextFieldAdapter';

interface EquipmentFormParams {
  equipment: IEquipment;
  onSubmit: any;
  disableEdit?: boolean;
}

const EquipmentForm = (params: EquipmentFormParams) => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const { equipment, onSubmit, disableEdit } = params;

  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);
  const [ivdrOptions, setIvdrOptions] = useState<IStatusOption[]>([]);
  const [warehouses, setWarehouses] = useState<IOption[]>([]);

  useEffect(() => {
    equipmentService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        setStatusOptions(
          res.actions.POST.status.choices.map((item) => {
            return {
              ...item,
              display_name: t(`${translationPrefix}.status_options.${item.display_name}`)
            };
          })
        );
        setIvdrOptions(res.actions.POST.ivdr_class.choices);
      }
    });

    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  return (
    <div>
      {equipment && (
        <ReactFinalForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!equipment.id}
          disableEdit={disableEdit}>
          <div className="flex flex-wrap">
            <div className="w-full p-2">
              <SectionTitle>{t('global.base-info')}</SectionTitle>
            </div>
            {/* <div className="w-full mb-4"> */}
            {/*   <IdentityField */}
            {/*     initValue={equipment.workstation} */}
            {/*     name="workstation" */}
            {/*     component={AutocompleteAdapter} */}
            {/*     options={workstations} */}
            {/*     label={t(`${translationPrefix}.workstation`)} */}
            {/*     type="text" */}
            {/*   /> */}
            {/* </div> */}
            <div className="w-1/4 pr-2 mb-4">
              <IdentityField
                name="code"
                defaultValue={equipment.code}
                component={TextFieldAdapter}
                label={t('global.code')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-1/4 pr-2 mb-4">
              <IdentityField
                name="internal_id"
                defaultValue={equipment.internal_id}
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.internal_id`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-1/2 pl-2 mb-4">
              <IdentityField
                name="description"
                defaultValue={equipment.description}
                component={TextFieldAdapter}
                label={t('global.description')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="pr-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="serial_number"
                defaultValue={equipment.serial_number}
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.serial_number`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="manufacturer"
                defaultValue={equipment.manufacturer}
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.manufacturer`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="status"
                label={t(`${translationPrefix}.status`)}
                initValue={
                  equipment.status
                    ? {
                        ...equipment.status,
                        display_name: t(
                          `${translationPrefix}.status_options.${equipment.status.display_name}`
                        )
                      }
                    : {
                        value: 1,
                        display_name: t(`${translationPrefix}.status_options.NEW`)
                      }
                }
                defaultValue={equipment.status ? equipment.status.value : 1}
                component={AutocompleteAdapter}
                options={statusOptions}
                placeholder="Status"
                type="text"
                optionLabel="display_name"
                optionValue="value"
                disabled={disableEdit}
              />
            </div>
            <div className="pl-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="ivdr_class"
                label={t(`${translationPrefix}.ivdr_class`)}
                initValue={equipment.ivdr_class ?? null}
                defaultValue={equipment.ivdr_class ? equipment.ivdr_class['value'] : null}
                component={AutocompleteAdapter}
                options={ivdrOptions}
                type="text"
                optionLabel="display_name"
                optionValue="value"
                disabled={disableEdit}
              />
            </div>
            <div className="pr-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="supplier"
                defaultValue={equipment.supplier}
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.supplier`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="position"
                defaultValue={equipment.position}
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.position`)}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="warehouse"
                component={AutocompleteAdapter}
                initValue={warehouses.find((s) => s.id === equipment.warehouse)}
                defaultValue={equipment.warehouse}
                options={warehouses}
                optionlabel="id"
                label={t('global.warehouse')}
                disabled={disableEdit}
                type="text"
              />
            </div>
            <div className="w-1/4 mb-4">
              <IdentityField
                name="market_value"
                format={(value) => value && value.replace('.', ',')}
                defaultValue={equipment.market_value}
                label={t(`${translationPrefix}.market_value`)}
                component={CurrencyTextFieldAdapter}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('global.misc')}</SectionTitle>
            </div>
            <div className="pr-2 w-full md:w-1/2 mb-4">
              <IdentityField
                name="transport_document_ref"
                component={TextFieldAdapter}
                label={t(`${translationPrefix}.transport_document_ref`)}
                type="text"
                initialValue={equipment?.transport_document_ref ?? ''}
                disabled={disableEdit}
              />
            </div>
            <div className="pl-2 w-full md:w-1/2 mb-4">
              <IdentityField
                name="delivery_date"
                component={DatePickerAdapter}
                label={t(`${translationPrefix}.delivery_date`)}
                type="date"
                initialValue={equipment?.delivery_date ?? null}
                disabled={disableEdit}
              />
            </div>
            <div className="pr-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="installation_date"
                component={DatePickerAdapter}
                label={t(`${translationPrefix}.installation_date`)}
                type="date"
                initialValue={equipment?.installation_date ?? null}
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="testing_date"
                component={DatePickerAdapter}
                label={t(`${translationPrefix}.testing_date`)}
                type="date"
                initialValue={equipment?.testing_date ?? null}
                disabled={disableEdit}
              />
            </div>
            <div className="px-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="buying_date"
                component={DatePickerAdapter}
                label={t(`${translationPrefix}.buying_date`)}
                type="date"
                disabled={disableEdit}
                initialValue={equipment?.buying_date ?? null}
              />
            </div>
            <div className="pl-2 w-full md:w-1/4 mb-4">
              <IdentityField
                name="decommission_date"
                component={DatePickerAdapter}
                label={t(`${translationPrefix}.decommission_date`)}
                type="date"
                initialValue={equipment?.decommission_date ?? null}
                disabled={disableEdit}
              />
            </div>
            <div className="w-full">
              <div className="mb-4">
                <SectionTitle>{t('pages.tickets.section-email')}</SectionTitle>
              </div>
              <div className="mb-4">
                <IdentityField
                  name="ticketing_email"
                  defaultValue={equipment.ticketing_email}
                  component={TextFieldAdapter}
                  label={t('global.ticketing_email')}
                  required={false}
                  type="text"
                />
              </div>
              <div className="mb-4">
                <IdentityField
                  name="cc_ticketing_email"
                  label={t(`${translationPrefix}.cc_ticketing_email`)}
                  placeholder="mario.verdi@mail.it; francesco.bianchi@mail.it; franco.rossi@mail.it"
                  initialValue={equipment.cc_ticketing_email}
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
              <div className="mb-4">
                <IdentityField
                  name="assistance_contact_number"
                  label={t(`${translationPrefix}.assistance_contact_number`)}
                  initialValue={equipment.assistance_contact_number}
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
            </div>
            <div className="w-full">
              <div className="mb-4">
                <IdentityField
                  name="accessories"
                  defaultValue={equipment.accessories}
                  component={TextFieldAdapter}
                  label={t(`${translationPrefix}.accessories`)}
                  type="text"
                  disabled={disableEdit}
                />
              </div>
            </div>
          </div>
          <div className="mb-4 w-full">
            <IdentityField
              name="notes"
              label={t(`global.notes`)}
              initialValue={equipment.notes}
              component={TextFieldAdapter}
              disabled={disableEdit}
            />
          </div>
        </ReactFinalForm>
      )}
    </div>
  );
};

export default EquipmentForm;
